import {
  apiPaths,
  type Draft,
  type DraftMessage,
  type Folder,
  type Mailbox,
  type OutboxEntry,
  type OutgoingMessage,
  type MailErrorBody,
  type MailErrorCode,
  type RecipientWarning,
  type SendResponse,
  type SignInState,
  type ThreadDetail,
  type ThreadMutation,
  type ThreadPage,
  type ThreadQuery,
} from '@gandhi/mail-protocol';

export type { Draft, DraftMessage };

/** The send body. `draftId` retires the stored draft once the message leaves the outbox. */
export type SendMessage = OutgoingMessage;

const draftPaths = {
  collection: (mailboxId: string) => `/api/mailboxes/${encodeURIComponent(mailboxId)}/drafts`,
  item: (mailboxId: string, draftId: string) =>
    `/api/mailboxes/${encodeURIComponent(mailboxId)}/drafts/${encodeURIComponent(draftId)}`,
};

export class ApiError extends Error {
  readonly code: MailErrorCode;
  readonly status: number;
  readonly details: Readonly<Record<string, unknown>>;

  constructor(status: number, body: MailErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.code;
    this.status = status;
    this.details = body.details ?? {};
  }
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * What the app is allowed to ask for. Named as an interface so tests can stand a
 * fake in its place — and so the surface stays free of any way to send without
 * naming a mailbox.
 */
export interface MailApiClient {
  /** Which mailboxes are open. Answers while locked, so the gate can read it first. */
  signInState(): Promise<SignInState>;
  /** One password. `address` names the mailbox when there is no session yet. */
  signIn(password: string, address?: string): Promise<SignInState>;
  /** Forget the password and close the mailboxes. There is no identity provider to leave. */
  signOut(): Promise<SignInState>;
  mailboxes(): Promise<{ mailboxes: Mailbox[] }>;
  folders(mailboxId: string): Promise<{ folders: Folder[] }>;
  threads(mailboxId: string, query?: ThreadQuery): Promise<ThreadPage>;
  thread(mailboxId: string, threadId: string): Promise<ThreadDetail>;
  mutateThread(
    mailboxId: string,
    threadId: string,
    mutation: ThreadMutation,
  ): Promise<ThreadDetail>;
  send(mailboxId: string, message: SendMessage): Promise<SendResponse>;
  createDraft(mailboxId: string, message: DraftMessage): Promise<{ draft: Draft }>;
  draft(mailboxId: string, draftId: string): Promise<{ draft: Draft }>;
  updateDraft(mailboxId: string, draftId: string, message: DraftMessage): Promise<{ draft: Draft }>;
  deleteDraft(mailboxId: string, draftId: string): Promise<void>;
  recipientWarnings(
    mailboxId: string,
    recipients: readonly string[],
  ): Promise<{ warnings: RecipientWarning[] }>;
  outbox(): Promise<{ entries: OutboxEntry[] }>;
  cancelSend(entryId: string): Promise<{ entry: OutboxEntry }>;
  attachmentUrl(mailboxId: string, messageId: string, attachmentId: string): string;
  /**
   * The part's bytes as a `data:` URL. Inline images are handed to the reading frame
   * this way because its content-security policy allows nothing else: it cannot fetch,
   * and a `blob:` URL minted here would not resolve inside it.
   */
  attachmentData(mailboxId: string, messageId: string, attachmentId: string): Promise<string>;
}

export interface MailApiOptions {
  readonly baseUrl?: string;
  readonly fetch?: FetchLike;
  /** Dev only. In production Caddy's forward-auth sets the identity header. */
  readonly user?: string;
}

/**
 * Every call names a mailbox, mirroring the routes. There is no client method that
 * sends without one, so "send as the other brand" has no expression here either.
 */
export class MailApi implements MailApiClient {
  readonly #baseUrl: string;
  readonly #fetch: FetchLike;
  readonly #user: string | undefined;

  constructor(options: MailApiOptions = {}) {
    this.#baseUrl = options.baseUrl ?? '';
    this.#fetch = options.fetch ?? ((input, init) => fetch(input, init));
    this.#user = options.user;
  }

  async #request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (init.body !== undefined) headers['content-type'] = 'application/json';
    if (this.#user !== undefined) headers['x-gandhi-user'] = this.#user;

    const response = await this.#fetch(`${this.#baseUrl}${path}`, {
      ...init,
      headers: { ...headers, ...(init.headers as Record<string, string> | undefined) },
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as MailErrorBody | null;
      throw new ApiError(
        response.status,
        body ?? { code: 'BACKEND_UNAVAILABLE', message: `Request failed (${response.status}).` },
      );
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  signInState(): Promise<SignInState> {
    return this.#request(apiPaths.signIn());
  }

  signIn(password: string, address?: string): Promise<SignInState> {
    return this.#request(apiPaths.signIn(), {
      method: 'POST',
      body: JSON.stringify(address === undefined ? { password } : { password, address }),
    });
  }

  signOut(): Promise<SignInState> {
    return this.#request(apiPaths.signIn(), { method: 'DELETE' });
  }

  mailboxes(): Promise<{ mailboxes: Mailbox[] }> {
    return this.#request(apiPaths.mailboxes());
  }

  folders(mailboxId: string): Promise<{ folders: Folder[] }> {
    return this.#request(apiPaths.folders(mailboxId));
  }

  threads(mailboxId: string, query: ThreadQuery = {}): Promise<ThreadPage> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    const suffix = params.size > 0 ? `?${params.toString()}` : '';
    return this.#request(`${apiPaths.threads(mailboxId)}${suffix}`);
  }

  thread(mailboxId: string, threadId: string): Promise<ThreadDetail> {
    return this.#request(apiPaths.thread(mailboxId, threadId));
  }

  mutateThread(
    mailboxId: string,
    threadId: string,
    mutation: ThreadMutation,
  ): Promise<ThreadDetail> {
    return this.#request(apiPaths.thread(mailboxId, threadId), {
      method: 'PATCH',
      body: JSON.stringify(mutation),
    });
  }

  send(mailboxId: string, message: SendMessage): Promise<SendResponse> {
    return this.#request(apiPaths.send(mailboxId), {
      method: 'POST',
      body: JSON.stringify(message),
    });
  }

  createDraft(mailboxId: string, message: DraftMessage): Promise<{ draft: Draft }> {
    return this.#request(draftPaths.collection(mailboxId), {
      method: 'POST',
      body: JSON.stringify(message),
    });
  }

  draft(mailboxId: string, draftId: string): Promise<{ draft: Draft }> {
    return this.#request(draftPaths.item(mailboxId, draftId));
  }

  updateDraft(
    mailboxId: string,
    draftId: string,
    message: DraftMessage,
  ): Promise<{ draft: Draft }> {
    return this.#request(draftPaths.item(mailboxId, draftId), {
      method: 'PUT',
      body: JSON.stringify(message),
    });
  }

  deleteDraft(mailboxId: string, draftId: string): Promise<void> {
    return this.#request(draftPaths.item(mailboxId, draftId), { method: 'DELETE' });
  }

  recipientWarnings(
    mailboxId: string,
    recipients: readonly string[],
  ): Promise<{ warnings: RecipientWarning[] }> {
    return this.#request(apiPaths.recipientWarnings(mailboxId), {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    });
  }

  outbox(): Promise<{ entries: OutboxEntry[] }> {
    return this.#request(apiPaths.outbox());
  }

  cancelSend(entryId: string): Promise<{ entry: OutboxEntry }> {
    return this.#request(apiPaths.cancelSend(entryId), { method: 'POST' });
  }

  attachmentUrl(mailboxId: string, messageId: string, attachmentId: string): string {
    return `${this.#baseUrl}${apiPaths.attachment(mailboxId, messageId, attachmentId)}`;
  }

  async attachmentData(
    mailboxId: string,
    messageId: string,
    attachmentId: string,
  ): Promise<string> {
    const response = await this.#fetch(this.attachmentUrl(mailboxId, messageId, attachmentId), {
      headers: this.#user === undefined ? {} : { 'x-gandhi-user': this.#user },
    });
    if (!response.ok)
      throw new ApiError(response.status, {
        code: 'ATTACHMENT_NOT_FOUND',
        message: `Part ${attachmentId} could not be read.`,
      });
    const type = response.headers.get('content-type') ?? 'application/octet-stream';
    const bytes = new Uint8Array(await response.arrayBuffer());
    let binary = '';
    for (let index = 0; index < bytes.length; index += 8192) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
    }
    return `data:${type};base64,${btoa(binary)}`;
  }
}
